import {
  CircularProgress
} from "@mui/material";
import _ from "lodash";
import React, { useEffect, useRef, useState } from "react";
import { t } from "ttag";
import { useStoreActions } from "../../models/RootStore";
import SimpleModal from "../../widgets/SimpleModal/SimpleModal";
import NavigationBar from "../NavigationBar/NavigationBar";
import DeviceInformationCard from "./DeviceInformationCard";
import useStyles from "./DeviceManagement.style";

interface IDevice {
  serial: any;
  pin: any;
  isConnected: boolean;
  id: string;
  protocolVersion: any;
  itcVersion: any;
  firmwareVersion: any;
  buildDate: any;
  site?: any;
  deviceType?: any;
  type?: any;
  disconnectedTimestamp?: any;
}

export default function DeviceManager(props: any) {
  const styles: any = useStyles();
  const upgradeFirmware = useStoreActions((actions) => actions.devices.upgradeFirmware);
  const firmwareStatus = useStoreActions((actions) => actions.devices.firmwareStatus);
  const [error, setError] = useState<any>(null);
  const [showBootAlert, setShowBootAlert] = useState<boolean>(false);
  const [device, setDevice] = useState<IDevice>({
    serial: "",
    pin: "",
    isConnected: false,
    protocolVersion: "protocolVersion",
    itcVersion: "itcVersion",
    firmwareVersion: "firmwareVersion",
    buildDate: "buildDate",
    disconnectedTimestamp: "disconnectedTimestamp",
    id: ""
  });
  const [fmStatus, setFmStatus] = useState<string>("");
  const [fmPercent, setFmPercent] = useState<string>("");
  let checkStatus = true;
  const getDevice = useStoreActions((actions) => actions.devices.getDeviceById);
  const otaRef = useRef<any>({});

  const {
    match: {
      params: { deviceId }
    }
  } = props;

  const isNGdevice = device.deviceType == 1 && device.type == 3;

  const checkStatusFMFc = async (deviceData: any) => {
    if (await otaRef.current.checkEdgeStatusOnEnter?.(deviceData)) {
      return;
    }

    const status = await firmwareStatus(deviceData.id);

    if (!status.data || (status.data && status.data.status === "")) {
      setFmStatus("");
      return;
    }

    const statusToShow = status.data.status !== "UPDATING" ? status.data.status : `${status.data.status}...`;
    setFmStatus(statusToShow);
    setFmPercent(
      status.data.percent != null && status.data.percent !== ""
        ? `${status.data.percent}% complete`
        : ""
    );
    if (status.data.status !== "UPDATED") {
      checkStatusFM(deviceData);
    }
  };

  const [checkStatusFM, setCheckStatusFM] = useState<any>(() => (deviceData: any) => setTimeout(async () => {
    if (!checkStatus) {
      return;
    }
    const status = await firmwareStatus(deviceData.id);
    const statusToShow = status.data.status !== "UPDATING" ? status.data.status : `${status.data.status}...`;
    setFmStatus(statusToShow);
    setFmPercent(
      status.data.percent != null && status.data.percent !== ""
        ? `${status.data.percent}% complete`
        : ""
    );

    if (status.data.status !== "UPDATED") {
      checkStatusFM(deviceData);
    } else {
      setTimeout(() => {
        setFmStatus("");
      }, 23 * 1000);
    }
  }, 3000));

  const fetchDevice = async (
    options: { skipStatusCheck?: boolean; expectedVersion?: string } = {}
  ) => {
    if (!deviceId) {
      return;
    }

    const res: any = await getDevice(deviceId);
    const nextDevice = otaRef.current.enrichDevice
      ? await otaRef.current.enrichDevice(res, options)
      : (setDevice(res), res);
    if (!options.skipStatusCheck) {
      checkStatusFMFc(nextDevice);
    }
    return nextDevice;
  };

  useEffect(() => {
    if (otaRef.current.fetchEnrichedDeviceRef) {
      otaRef.current.fetchEnrichedDeviceRef.current = async (options = {}) => {
        if (!deviceId) {
          return;
        }
        const res: any = await getDevice(deviceId);
        return otaRef.current.enrichDevice(res, options);
      };
    }
  });

  useEffect(() => {
    fetchDevice();

    return () => {
      checkStatus = false;
      setCheckStatusFM(null);
      otaRef.current.cleanup?.();
    };
  }, [deviceId]);

  const onFileLoad = (event: any) => {
    if (event.target) {
      const file = event.target.files[0];
      const data = new FormData();
      data.append("file", file);

      upgradeFirmware({ deviceId: device.id, command: data });
      setFmStatus("Starting...");
      checkStatusFM(device);

      event.target.value = "";
    } else {
      console.log("something went wrong while loading the file", event);
    }
  };

  return (
    <NavigationBar
      navTitle={t`Device Management`}
      pageTitle={t`Device Management`}
      {...props}
    >
      <div className={styles.editDeviceWrapper}>
        <SimpleModal
          showModal={!!error}
          title={t`Error had happened`}
          contentText={error ? error : ""}
          actionName={t`OK`}
          onAction={() => {
            setError(null);
          }}
          closeModal={() => {
            setError(null);
          }}
        />

        <SimpleModal
          showModal={showBootAlert}
          title={t`Missing Boot alert`}
          contentText={t`Changes will not be set without booting the device. Are you sure you want to leave?`}
          actionName={t`Leave`}
          onAction={() => {
            props.onClose();
          }}
          closeModal={() => {
            setShowBootAlert(false);
          }}
        />
        <SimpleModal
          showModal={false}
          title={t`Booting, please wait...`}
          actionName={t`OK`}
          onAction={_.noop}
          closeModal={_.noop}
          hideBtns={true}
        >
          <div className={styles.bootWrapper}>
            <CircularProgress />
          </div>
        </SimpleModal>

        {!_.isEmpty(device) && (
          <DeviceInformationCard
            device={device}
            setDevice={setDevice}
            deviceId={deviceId}
            history={props.history}
            companion="auto"
            ownOtaLifecycle={false}
            otaRef={otaRef}
            onRetryConnection={fetchDevice}
            fmStatus={fmStatus}
            fmPercent={fmPercent}
            onFileLoad={isNGdevice ? undefined : onFileLoad}
          />
        )}
      </div>
    </NavigationBar>
  );
}
